/**
 * /accounts/:aid/order — admin-only. Buy button hardware via Stripe
 * Checkout, and see the live status of past orders.
 *
 * No payment is taken here — `create_hardware_checkout` returns a hosted
 * Stripe Checkout URL we redirect to. On return, `?checkout=success`
 * shows a confirmation; the paid order lands in `accounts/:aid/orders`
 * via the Stripe webhook and appears in the list below (live).
 *
 * Billing starts later: the monthly subscription begins on first device
 * connect (see functions/billing.py), not at purchase.
 */

import { useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { useMyRole } from '@/hooks/useMyRole';
import { useAccount } from '@/hooks/useAccount';
import { useOrders, type OrderWithId } from '@/hooks/useOrders';
import { Button } from '@/components/ui/Button';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { Field } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/Spinner';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { createHardwareCheckout } from '@/lib/callables';
import { firePurchaseConversion, ESTIMATED_PURCHASE_VALUE_GBP } from '@/lib/ads';
import { track } from '@/lib/analytics';
import {
  BUTTON_PRICE_GBP,
  MONTHLY_GBP,
  INCLUDED_TRIAL_DAYS,
  MAX_BUTTONS_PER_ACCOUNT,
} from '@/lib/pricing';
import { OrdersList } from '@/components/OrdersList';

export function OrderPage() {
  const { aid } = useParams<{ aid: string }>();
  const role = useMyRole(aid ?? null);
  const { account, loading: accountLoading } = useAccount(aid ?? null);
  const { orders } = useOrders(aid ?? null);
  const [searchParams] = useSearchParams();
  const checkoutResult = searchParams.get('checkout');
  const sessionId = searchParams.get('session_id');

  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fire the secondary Ads purchase conversion. Stripe only redirects to
  // ?checkout=success&session_id=<id> after a confirmed payment, so we can fire
  // immediately — no need to wait for the webhook-written order doc. We report a
  // flat estimated customer value (ESTIMATED_PURCHASE_VALUE_GBP), not the Stripe
  // charge: hardware is ~break-even, the value is the subscription, and the
  // charge scales with quantity while customer value doesn't. The session id is
  // the transaction_id (Google de-dup) and the single-fire key.
  useEffect(() => {
    if (checkoutResult !== 'success' || !sessionId) return;
    firePurchaseConversion(sessionId, ESTIMATED_PURCHASE_VALUE_GBP);
  }, [checkoutResult, sessionId]);

  if (!aid) return <Navigate to="/" replace />;

  // Wait for role to resolve before deciding what to show. `null` can
  // mean "still loading" or "not a member"; the wait is short because
  // members are read from the per-account snapshot listener.
  if (role === null && !accountLoading) {
    return <Navigate to="/" replace />;
  }
  if (role === null || accountLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (role !== 'admin') {
    return (
      <Card className="mx-auto max-w-lg">
        <CardHeader>
          <CardTitle>Buy a button</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-ink-soft">
            Only the account admin can buy a button. Ask the admin on this
            account to do this from their sign-in.
          </p>
          <p className="mt-4 text-sm">
            <Link to="/" className="text-accent-700 hover:underline">
              ← Back to dashboard
            </Link>
          </p>
        </CardBody>
      </Card>
    );
  }

  const hasOrders = orders.length > 0;

  async function startCheckout() {
    if (!aid) return;
    setBusy(true);
    setError(null);
    track('checkout_started', { quantity });
    try {
      const result = await createHardwareCheckout({ accountId: aid, quantity });
      // Hand off to Stripe's hosted Checkout page.
      window.location.href = result.data.url;
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^FirebaseError:\s*/, '') : 'Checkout failed.');
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-6">
      {checkoutResult === 'success' && (
        <div className="rounded-card border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          Payment received — thank you! Your order will appear below in a
          moment. We'll prepare your button and post it out.
        </div>
      )}
      {checkoutResult === 'cancelled' && (
        <div className="rounded-card border border-paper-border bg-paper-alt px-4 py-3 text-sm text-ink-soft">
          Checkout cancelled — no payment was taken. You can try again any time.
        </div>
      )}

      {hasOrders && <OrdersCard orders={orders} elderName={account?.elderName} />}

      {/* Hide the buy form right after a successful checkout — the success
          banner already says the order will appear below, so a fresh "buy"
          form reads like the payment didn't take. Revisiting the page (no
          ?checkout=success) shows "Buy another button" again. */}
      {checkoutResult !== 'success' && (
      <>
      <Card>
        <CardHeader>
          <CardTitle>{hasOrders ? 'Buy another button' : 'Buy your button'}</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="rounded-card border border-paper-border bg-paper-alt p-4 text-sm">
            {hasOrders ? (
              <>
                <div className="font-semibold text-ink">Adding another button</div>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-soft">
                  <li>
                    <strong>£{BUTTON_PRICE_GBP}</strong> per button (one-off).
                  </li>
                  <li>
                    <strong>No extra monthly fee</strong> — your{' '}
                    £{MONTHLY_GBP.toFixed(2)}/month subscription already covers
                    every button
                    {account?.elderName ? ` for ${account.elderName}` : ''}.
                  </li>
                </ul>
              </>
            ) : (
              <>
                <div className="font-semibold text-ink">How pricing works</div>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-soft">
                  <li>
                    <strong>£{BUTTON_PRICE_GBP}</strong> per button (one-off),
                    including your first 30 days of service.
                  </li>
                  <li>
                    Then <strong>£{MONTHLY_GBP.toFixed(2)}/month</strong> per
                    person, cancel any time — covers all your buttons.
                  </li>
                  <li>
                    The monthly meter starts when you{' '}
                    <strong>set up your button</strong> — not when you buy. You
                    get a full {INCLUDED_TRIAL_DAYS} days from then.
                  </li>
                </ul>
              </>
            )}
          </div>

          <form
            className="mt-6 flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void startCheckout();
            }}
          >
            <Field
              label="How many buttons?"
              htmlFor="quantity"
              hint={`Up to ${MAX_BUTTONS_PER_ACCOUNT} per person.`}
            >
              <select
                id="quantity"
                className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value, 10))}
              >
                {Array.from({ length: MAX_BUTTONS_PER_ACCOUNT }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? 'button' : 'buttons'}
                  </option>
                ))}
              </select>
            </Field>

            <div className="flex items-baseline justify-between rounded-card border border-paper-border bg-white px-4 py-3 text-sm">
              <span className="text-ink-soft">One-off total</span>
              <span className="font-semibold text-ink">
                £{quantity * BUTTON_PRICE_GBP}
              </span>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button type="submit" disabled={busy}>
              {busy ? 'Redirecting to payment…' : 'Continue to payment'}
            </Button>
            <p className="text-xs text-ink-soft">
              We currently ship to <strong>UK addresses only</strong>. You'll
              be taken to our payment provider (Stripe) to pay securely and
              enter your delivery address.
              {!hasOrders && (
                <>
                  {' '}
                  Your card is saved so your subscription can start once the
                  button is set up — you won't be charged the monthly fee until
                  then.
                </>
              )}
            </p>
            {/* Pre-contract information the Consumer Contracts Regulations
                require at the point of sale: terms incorporation, delivery
                time, the 14-day right, who pays return postage, and the
                express request to start service inside the cooling-off
                window (the subscription begins at device setup). Without the
                14-day disclosure the cancellation period extends to 12
                months and the diminished-value deduction in terms §10 is
                lost; without the return-postage line the buyer doesn't have
                to pay it — don't remove. */}
            <p className="text-xs text-ink-soft">
              We aim to dispatch within <strong>5 working days</strong>. By
              placing an order you agree to our{' '}
              <ExternalLink href="https://remindmevoice.com/terms" className="underline">
                Terms of Use
              </ExternalLink>
              , and you can change your mind within <strong>14 days of
              delivery</strong> — you'd just pay the return postage. See{' '}
              <ExternalLink
                href="https://remindmevoice.com/terms#cancellation"
                className="underline"
              >
                your cancellation rights
              </ExternalLink>
              . Ordering also asks us to start your service when you set the
              button up, even if that's within the 14 days — your statutory
              rights aren't affected. Security updates:{' '}
              <ExternalLink
                href="https://remindmevoice.com/security"
                className="underline"
              >
                how long the button is supported
              </ExternalLink>
              .
            </p>
          </form>
        </CardBody>
      </Card>
      <ByoOptionCard aid={aid} />
      </>
      )}
    </div>
  );
}

// BYO (browser-flash your own Voice PE) DISABLED 2026-07-16: while insurance
// and compliance cover UK customers only, we don't offer a route that lets
// anyone anywhere self-provision service. International interest goes to
// hello@ instead. The BYO machinery (ByoSetup.tsx, byoFlasher.ts) is kept
// for when we re-open it — this card is the replacement entry point.
function ByoOptionCard({ aid: _aid }: { aid: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Outside the UK?</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-sm text-ink-soft">
          RemindMeVoice is available in the UK only for now. If you'd like it
          where you are, email{' '}
          <a
            href="mailto:hello@remindmevoice.com?subject=RemindMeVoice%20outside%20the%20UK"
            className="font-medium text-accent-700 underline"
          >
            hello@remindmevoice.com
          </a>{' '}
          and we'll let you know as soon as it's available in your country.
        </p>
      </CardBody>
    </Card>
  );
}

function OrdersCard({
  orders,
  elderName,
}: {
  orders: OrderWithId[];
  elderName?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Your orders</CardTitle>
      </CardHeader>
      <CardBody>
        <OrdersList orders={orders} elderName={elderName} />
      </CardBody>
    </Card>
  );
}
